/**
 * Persistent sidebar for map settings (desktop only)
 */

import { MapContent } from './MapContent';
import { cn } from '@/lib/utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface GeographicEntityWithRelevance extends GeographicEntity {
    relevanceType?: string;
    relevanceReason?: string;
}

interface EventMarkerData {
    event: HistoricalEventByPassage | HistoricalEvent;
    position: unknown;
    matchingPlace: GeographicEntity;
}

interface MapSidebarProps {
    isOpen: boolean;
    selectedStyleId: string;
    onStyleChange: (styleId: string) => void;
    showLabels: boolean;
    onShowLabelsChange: (show: boolean) => void;
    showAncientRoads: boolean;
    onShowAncientRoadsChange: (show: boolean) => void;
    showSeaLanes: boolean;
    onShowSeaLanesChange: (show: boolean) => void;
    showRomanProvinces: boolean;
    onShowRomanProvincesChange: (show: boolean) => void;
    showAncientEmpires: boolean;
    onShowAncientEmpiresChange: (show: boolean) => void;
    passageEventsOnly: boolean;
    onPassageEventsOnlyChange: (show: boolean) => void;
    availablePlaceTypes: string[];
    selectedPlaceTypes: Set<string>;
    onPlaceTypesChange: (types: Set<string>) => void;
    placeTypeCounts: Map<string, number>;
    availableEventCategories: string[];
    selectedEventCategories: Set<string>;
    onEventCategoriesChange: (categories: Set<string>) => void;
    eventCategoryCounts: Map<string, number>;
    visiblePlaces: GeographicEntityWithRelevance[];
    eventMarkers: EventMarkerData[];
    onPlaceClick: (placeName: string, coordinates: { latitude: number; longitude: number }) => void;
    onEventClick: (eventName: string, coordinates: { latitude: number; longitude: number }) => void;
    passageYear?: number | null;
}

export function MapSidebar(props: MapSidebarProps) {
    const isDarkMode = useDarkMode();

    if (!props.isOpen) return null;

    return (
        <div className={cn(
            "hidden md:flex flex-col w-80 h-full border-r relative z-10 transition-all",
            isDarkMode
                ? "bg-black/80 border-white/10 text-white"
                : "bg-white border-black/10 shadow-lg text-slate-900"
        )}>
            {/* Header */}
            <div className={cn(
                "flex items-center px-4 py-4 border-b sticky top-0 z-10",
                isDarkMode
                    ? "border-white/20 bg-black/90"
                    : "border-black/10 bg-white/95"
            )}>
                <h3 className="text-sm font-bold uppercase tracking-wider opacity-90">Map Settings</h3>
            </div>

            <div className="flex-1 overflow-y-auto">
                <MapContent {...props} />
            </div>
        </div>
    );
}
